import { useMemo, useRef, useState } from "react";

import { Button, Card, CardBody, CardHeader, Flex, IconButton, useToken } from "@chakra-ui/react";
import { IconForms, IconPlus, IconTextWrap, IconTrash } from "@tabler/icons-react";
import { animateView, DynamicForm, ExpandedJSONSchema7, getButtonStylesFromColor, Textarea } from "am-tax-fe-core";
import kebabCase from "lodash.kebabcase";

import { Question } from "../../api";

const RequestQuestionnaireForm = ({
    questions,
    onQuestionChange,
    isEditing = false,
}: {
    questions: Question[];
    onQuestionChange: (questions: Question[]) => void;
    isEditing?: boolean;
}) => {
    const [editAsRawText, setEditAsRawText] = useState(false);

    const localQuestions = useRef(questions);

    const toggleInputMode = () => {
        animateView(() => {
            setEditAsRawText(!editAsRawText);
        });
    };

    const stringyQuestions = useMemo(() => {
        return JSON.stringify(questions.map(q => q.question));
    }, [questions]);

    const viewQuestionnaireSchema = useMemo(() => {
        return {
            type: "object",
            additionalProperties: true,
            properties: questions.reduce(
                (acc, question) => {
                    const key = question.id ?? kebabCase(question.question ?? undefined);
                    acc![key] = {
                        type: "string",
                        title: question.question ?? undefined,
                        format: "textarea",
                        flexStyle: "1 0 100%",
                    };
                    return acc;
                },
                {} as ExpandedJSONSchema7["properties"],
            ),
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stringyQuestions]);

    const editQuestionnaireSchema = useMemo(() => {
        return {
            type: "object",
            additionalProperties: true,
            properties: questions.reduce(
                (acc, question, i) => {
                    acc![question.order ?? i] = {
                        type: "string",
                        title: `Question ${(question.order ?? i) + 1}`,
                        flexStyle: "1 0 100%",
                        description: question.answer ?? undefined,
                    };
                    return acc;
                },
                {} as ExpandedJSONSchema7["properties"],
            ),
        };
    }, [questions]);

    const questionsAsDynamicFormValues = useMemo(() => {
        return questions.reduce(
            (acc, question, i) => {
                acc[question.order ?? i] = question.question ?? "";
                return acc;
            },
            {} as Record<number, string>,
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stringyQuestions]);

    const answersAsDynamicFormValues = useMemo(() => {
        return questions.reduce(
            (acc, question) => {
                const key = question.id ?? kebabCase(question.question ?? undefined);
                acc[key] = question.answer ?? "";
                return acc;
            },
            {} as Record<string, string>,
        );
    }, [questions]);

    const handleAddQuestion = () => {
        onQuestionChange([...questions, { id: "", question: "", answer: "", order: questions.length }]);
    };

    const gray800 = useToken("colors", "gray.800");

    return isEditing ? (
        <Card my="1rem" style={{ viewTransitionName: "data-request-card" }}>
            <CardHeader display="flex" alignItems="center" margin="0" justifyContent="space-between">
                <h4>Edit Questionnaire</h4>
                <Button
                    variant="outline"
                    colorScheme="blue.400"
                    rightIcon={editAsRawText ? <IconForms /> : <IconTextWrap />}
                    size="sm"
                    onClick={toggleInputMode}
                    {...getButtonStylesFromColor(gray800).outline}
                >
                    {editAsRawText ? "Edit in Form" : "Edit as Text"}
                </Button>
            </CardHeader>
            <CardBody>
                {editAsRawText ? (
                    <Textarea
                        height={`${questions.length * 1.25 + 2}rem`}
                        resize="vertical"
                        defaultValue={questions.map(q => q.question).join("\n")}
                        marginBottom="2rem"
                        onChange={e =>
                            onQuestionChange(
                                e.target.value
                                    .split("\n")
                                    .map((q, i) => ({ id: questions[i]?.id || "", question: q, answer: questions[i]?.answer || "", order: i })),
                            )
                        }
                        onKeyDown={e => {
                            if (e.key === "Enter") {
                                handleAddQuestion();
                            }
                        }}
                    />
                ) : (
                    <>
                        <DynamicForm
                            defaultValues={questionsAsDynamicFormValues}
                            // @ts-ignore
                            formSchema={editQuestionnaireSchema}
                            CustomFieldComponent={props => (
                                <Flex width="100%" gap="1rem">
                                    {props.children}
                                    <IconButton
                                        mt="2rem"
                                        icon={<IconTrash />}
                                        aria-label="delete"
                                        variant="ghost"
                                        onClick={() => {
                                            onQuestionChange(questions.filter(q => props.name !== `${q.order}`));
                                            localQuestions.current = questions.filter(q => props.name !== `${q.order}`);
                                        }}
                                    />
                                </Flex>
                            )}
                            onBlur={() => onQuestionChange(localQuestions.current)}
                            onChange={values =>
                                (localQuestions.current = Object.values(values)
                                    .filter(v => v !== undefined)
                                    .map((value, i) => ({ id: questions[i]?.id, question: value, answer: questions[i]?.answer, order: i })))
                            }
                            showErrorCount={false}
                            showReset={false}
                            showSubmit={false}
                        />
                        <Flex justifyContent="flex-end">
                            <IconButton
                                aria-label="Add question"
                                variant="outline"
                                icon={<IconPlus />}
                                onClick={handleAddQuestion}
                                {...getButtonStylesFromColor(gray800).outline}
                            />
                        </Flex>
                    </>
                )}
            </CardBody>
        </Card>
    ) : (
        <Card>
            <CardHeader>
                <h4>Requested Information</h4>
            </CardHeader>
            <CardBody>
                <DynamicForm
                    defaultValues={answersAsDynamicFormValues}
                    // @ts-ignore
                    formSchema={viewQuestionnaireSchema}
                    onChange={values =>
                        (localQuestions.current = Object.values(values)
                            .filter(v => v !== undefined)
                            .map((value, i) => ({ ...questions[i], answer: value })))
                    }
                    onBlur={() => onQuestionChange(localQuestions.current)}
                    showErrorCount={false}
                    showReset={false}
                    showSubmit={false}
                />
            </CardBody>
        </Card>
    );
};

export default RequestQuestionnaireForm;
